import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslator } from "@sia/style-guide";
import PGAdapter from "common/Adapters/PGAdapter/PGAdapter";
import GlobalContext from "contexts/GlobalContext";
import { MasterKeyContext } from "contexts/MasterKeyContext";
import { buildDropdownOptionsEmployees, createEmptyCollectionData, defaultCompanyInput, defaultSelection } from "./collectionData.helpers";

const trustee = { name: process.env.REACT_APP_BENCHMARKING_SURVEY_TRUSTEE_NAME };
const emptySelection = () => ({ id: 0, nogaCode: null, label: "" });
const hasValue = (value) => value !== null && value !== undefined && value !== "";
const sameId = (left, right) => hasValue(left) && hasValue(right) && String(left) === String(right);
const findById = (items, id, fallback) => (items || []).find((item) => sameId(item.id, id)) || fallback;
const getErrorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;
export function useCollectionData({ surveyAdapter }) {
  const { language: lang } = useContext(GlobalContext);
  const { isMasterKeyValid, cryptoSessionRevision } = useContext(MasterKeyContext);
  const translate = useTranslator();
  const translateRef = useRef(translate);
  const initialLoadRequestRef = useRef(0);
  const employeeFunctionsRequestRef = useRef(0);
  translateRef.current = translate;
  const isInputUser = Boolean(sessionStorage.getItem("sia.benchmarking.role-input"));
  const canLoadCollectionData = Boolean(isInputUser && lang && isMasterKeyValid);
  const collectionScopeKey = `${cryptoSessionRevision}:${lang || ""}`;
  const [companyDetailId, setCompanyDetailId] = useState(null);
  const [companyInput, setCompanyInput] = useState(defaultCompanyInput);
  const [companyId, setCompanyId] = useState(null);
  const [nogaList, setNogaList] = useState([]);
  const [cantonsList, setCantonsList] = useState([]);
  const [officeSizeList, setOfficeSizeList] = useState([]);
  const [selectedNoga, setSelectedNoga] = useState(defaultSelection);
  const [selectedCanton, setSelectedCanton] = useState(defaultSelection);
  const [selectedOfficeSize, setSelectedOfficeSize] = useState(defaultSelection);
  const [collectionData, setCollectionData] = useState(createEmptyCollectionData);
  const [surveyConcluded, setSurveyConcluded] = useState(false);
  const [reviewPhase, setReviewPhase] = useState(false);
  const [isSurveyFinished, setIsSurveyFinished] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(false);
  const [clientReply, setClientReply] = useState("");
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [showEmployeeEntryForm, setShowEmployeeEntryForm] = useState(true);
  const [employeeEntryFormItem, setEmployeeEntryFormItem] = useState({});
  const [showEmployeesFileUpload, setShowEmployeesFileUpload] = useState(false);
  const [isCollectionDataLoading, setIsCollectionDataLoading] = useState(canLoadCollectionData);
  const [collectionDataLoadError, setCollectionDataLoadError] = useState(null);
  const [collectionDataLoadVersion, setCollectionDataLoadVersion] = useState(0);
  const [loadedCollectionScopeKey, setLoadedCollectionScopeKey] = useState(null);
  const [collectionDataErrorScopeKey, setCollectionDataErrorScopeKey] = useState(null);
  const [isEmployeeFunctionsLoading, setIsEmployeeFunctionsLoading] = useState(false);
  const [employeeFunctionsLoadError, setEmployeeFunctionsLoadError] = useState(null);

  const hasCurrentCollectionSnapshot = Boolean(canLoadCollectionData && loadedCollectionScopeKey === collectionScopeKey);
  const visibleCollectionData = useMemo(
    () => (hasCurrentCollectionSnapshot ? collectionData : createEmptyCollectionData()),
    [collectionData, hasCurrentCollectionSnapshot]
  );
  const dropdownOptionsEmployees = useMemo(() => buildDropdownOptionsEmployees(visibleCollectionData), [visibleCollectionData]);

  const resetEmployeeUi = useCallback((showEntryForm) => {
    setEmployeeEntryFormItem({});
    setShowEmployeesFileUpload(false);
    setShowEmployeeEntryForm(showEntryForm);
  }, []);

  const clearLoadedState = useCallback(() => {
    employeeFunctionsRequestRef.current += 1;
    setLoadedCollectionScopeKey(null);
    setCompanyDetailId(null);
    setCompanyInput(defaultCompanyInput);
    setCompanyId(null);
    setNogaList([]);
    setCantonsList([]);
    setOfficeSizeList([]);
    setSelectedNoga(emptySelection());
    setSelectedCanton(emptySelection());
    setSelectedOfficeSize(emptySelection());
    setCollectionData(createEmptyCollectionData());
    setSurveyConcluded(false);
    setReviewPhase(false);
    setIsSurveyFinished(false);
    setIsFirstTimeUser(false);
    setIsInitialLoad(false);
    setClientReply("");
    setIsSubmitModalOpen(false);
    setIsEmployeeFunctionsLoading(false);
    setEmployeeFunctionsLoadError(null);
    resetEmployeeUi(true);
  }, [resetEmployeeUi]);

  const applyLoadedState = useCallback(
    (data, scopeKey) => {
      const hasCompanyDetail = Boolean(data.companyDetail);
      const hasCompanyInput = Boolean(data.companyInput);
      if (hasCompanyDetail !== hasCompanyInput)
        throw new Error("Survey data is incomplete: company detail and company input must both exist or both be absent.");
      const isFirstTime = !hasCompanyDetail;
      const nextCompanyInput = isFirstTime ? defaultCompanyInput : data.companyInput;
      const employees = isFirstTime ? [] : data.employees || [];
      setCompanyDetailId(isFirstTime ? null : data.companyDetail.id);

      setCompanyInput(nextCompanyInput);
      setCompanyId(data.companyId ?? null);
      setNogaList(data.nogaList || []);
      setCantonsList(data.cantonList || []);
      setOfficeSizeList(data.officeSizeList || []);
      setSelectedNoga(isFirstTime ? emptySelection() : data.selectedNoga || null);
      setSelectedCanton(isFirstTime ? emptySelection() : data.selectedCanton || null);
      setSelectedOfficeSize(isFirstTime ? emptySelection() : data.selectedOfficeSize || null);
      setReviewPhase(isFirstTime ? false : Boolean(data.reviewPhase));
      setSurveyConcluded(isFirstTime ? false : Boolean(data.surveyConcluded));
      setIsSurveyFinished(isFirstTime ? false : Boolean(data.isSurveyFinished));
      setIsFirstTimeUser(isFirstTime);
      setIsInitialLoad(isFirstTime);
      setIsEmployeeFunctionsLoading(false);
      setEmployeeFunctionsLoadError(null);
      setCollectionData({
        trustee,
        ...(isFirstTime ? {} : { companyHash: nextCompanyInput.companyHash, companyInputId: nextCompanyInput.id }),
        survey: data.survey,
        employees,
        companyOverheads: isFirstTime ? [] : data.companyOverheads || [],
        allEmployeeFunctions: data.allEmployeeFunctions,
        employeeFunctions: data.employeeFunctions,
        employmentLevels: data.employmentLevels,
        employmentStatuses: data.employmentStatuses,
        employeeSexes: data.employeeSexes,
        employmentTypes: data.employmentTypes,
      });
      resetEmployeeUi(employees.length === 0);
      setLoadedCollectionScopeKey(scopeKey);
    },
    [resetEmployeeUi]
  );

  const retryCollectionDataLoad = useCallback(() => {
    setLoadedCollectionScopeKey(null);
    setCollectionDataLoadError(null);
    setCollectionDataErrorScopeKey(null);
    setCollectionDataLoadVersion((version) => version + 1);
  }, []);

  useEffect(() => {
    if (!canLoadCollectionData) {
      initialLoadRequestRef.current += 1;
      clearLoadedState();
      setIsCollectionDataLoading(false);
      setCollectionDataLoadError(null);
      setCollectionDataErrorScopeKey(null);
      return undefined;
    }
    const requestId = ++initialLoadRequestRef.current;
    const requestScopeKey = collectionScopeKey;
    clearLoadedState();
    setIsCollectionDataLoading(true);
    setCollectionDataLoadError(null);
    setCollectionDataErrorScopeKey(null);
    (async () => {
      try {
        const data = await surveyAdapter.fetchData(translateRef.current);
        if (requestId !== initialLoadRequestRef.current) return;
        applyLoadedState(data, requestScopeKey);
      } catch (error) {
        if (requestId !== initialLoadRequestRef.current) return;
        const message = getErrorMessage(error, "Failed to load survey collection data.");
        console.error("Failed to load survey collection data.", {
          message,
          status: error?.response?.status,
        });
        setCollectionDataLoadError(message);
        setCollectionDataErrorScopeKey(requestScopeKey);
      } finally {
        if (requestId === initialLoadRequestRef.current) setIsCollectionDataLoading(false);
      }
    })();
    return () => {
      if (requestId === initialLoadRequestRef.current) initialLoadRequestRef.current += 1;
    };
  }, [applyLoadedState, canLoadCollectionData, clearLoadedState, collectionDataLoadVersion, collectionScopeKey, surveyAdapter]);

  const resetEmployeeFunctions = useCallback(
    async (noga) => {
      const requestId = ++employeeFunctionsRequestRef.current;
      setEmployeeFunctionsLoadError(null);
      setCollectionData((state) => ({
        ...state,
        employeeFunctions: [],
      }));
      if (!noga?.id) {
        setIsEmployeeFunctionsLoading(false);
        return [];
      }
      setIsEmployeeFunctionsLoading(true);
      try {
        const employeeFunctions = await surveyAdapter.fetchEmployeeFunctionsForNoga(noga.id);
        if (requestId !== employeeFunctionsRequestRef.current) return null;
        const nextEmployeeFunctions = employeeFunctions || [];
        setCollectionData((state) => ({ ...state, employeeFunctions: nextEmployeeFunctions }));
        return nextEmployeeFunctions;
      } catch (error) {
        if (requestId !== employeeFunctionsRequestRef.current) return null;
        const message = getErrorMessage(error, "Failed to load employee functions.");
        console.error("Failed to load employee functions.", { message, status: error?.response?.status });
        setEmployeeFunctionsLoadError(message);
        return null;
      } finally {
        if (requestId === employeeFunctionsRequestRef.current) {
          setIsEmployeeFunctionsLoading(false);
        }
      }
    },
    [surveyAdapter]
  );

  const setSelectedOption = useCallback((field, option) => {
    if (field === "noga") setSelectedNoga(option);
    else if (field === "canton") setSelectedCanton(option);
    else if (field === "office-size") setSelectedOfficeSize(option);
  }, []);

  const applyUpdatedOption = useCallback(
    (field, data, fallback) => {
      if (field === "noga") {
        const noga = findById(nogaList, data.nogaId, fallback);
        setSelectedNoga(noga);
        resetEmployeeFunctions(noga);
      } else if (field === "canton") {
        setSelectedCanton(findById(cantonsList, data.cantonId, fallback));
      } else if (field === "office-size") {
        setSelectedOfficeSize(findById(officeSizeList, data.officeSizeId, fallback));
      }
    },
    [cantonsList, nogaList, officeSizeList, resetEmployeeFunctions]
  );

  const submitOptions = useCallback(
    async (field, option) => {
      if (!isFirstTimeUser) {
        return surveyAdapter.updateCompanyDetails(field, companyDetailId, companyId, companyInput, option, selectedNoga, selectedOfficeSize, (data) =>
          applyUpdatedOption(field, data, option)
        );
      }
      const nextNoga = field === "noga" ? option : selectedNoga;
      const nextCanton = field === "canton" ? option : selectedCanton;
      const nextOfficeSize = field === "office-size" ? option : selectedOfficeSize;
      setSelectedOption(field, option);
      if (!nextNoga?.id || !nextCanton?.id || !nextOfficeSize?.id) return undefined;

      const template = await surveyAdapter.fetchNewTemplateAndDefineCompanyHash({
        nogaCodeObj: nextNoga,
        cantonObj: nextCanton,
        officeSizeObj: nextOfficeSize,
        survey: collectionData?.survey,
        translate,
        companyId,
      });
      if (!template?.companyDetail || !template?.companyInput) return undefined;
      const {
        cantonObj,
        officeSizeObj,
        companyDetail,
        companyInput: nextCompanyInput,
        companyOverheads,
        employees = [],
        companyId: nextCompanyId,
      } = template;
      const noga = findById(nogaList, companyDetail.nogaId, nextNoga);
      const canton = findById(cantonsList, cantonObj?.id, nextCanton);
      const officeSize = findById(officeSizeList, officeSizeObj?.id, nextOfficeSize);

      setCompanyId(nextCompanyId);
      setSelectedNoga(noga);
      setSelectedCanton(canton);
      setSelectedOfficeSize(officeSize);
      setCompanyDetailId(companyDetail.id);
      setCompanyInput(nextCompanyInput);
      setIsFirstTimeUser(false);
      setCollectionData((previous) => ({
        ...previous,
        companyDetail,
        companyInputId: nextCompanyInput.id,
        companyHash: nextCompanyInput.companyHash,
        companyOverheads: companyOverheads || [],
        employees,
      }));
      resetEmployeeUi(employees.length === 0);
      resetEmployeeFunctions(noga);
      PGAdapter.request("POST", "notification/internal/add/collecting", null, () => null);
      return template;
    },
    [
      applyUpdatedOption,
      cantonsList,
      collectionData?.survey,
      companyDetailId,
      companyId,
      companyInput,
      isFirstTimeUser,
      nogaList,
      officeSizeList,
      resetEmployeeFunctions,
      resetEmployeeUi,
      selectedCanton,
      selectedNoga,
      selectedOfficeSize,
      setSelectedOption,
      surveyAdapter,
      translate,
    ]
  );

  const finishDataCollection = useCallback(async () => {
    const surveyId = collectionData?.survey?.id;
    const submitted = await surveyAdapter.submitDataForReview(surveyId, companyInput, companyId, clientReply);
    if (!submitted) return false;
    setReviewPhase(true);
    const companyHash = typeof surveyAdapter.getCompanyHash === "function" ? surveyAdapter.getCompanyHash() : null;
    PGAdapter.request("POST", "notification/add", { company_hash: companyHash, survey_id: surveyId }, () => {
      PGAdapter.request("POST", "notification/internal/remove", null, () => null);
    });
    return true;
  }, [clientReply, collectionData?.survey?.id, companyId, companyInput, surveyAdapter]);

  const reopenSubmittedSurvey = useCallback(async () => {
    const reopened = await surveyAdapter.reopenSubmittedSurvey(collectionData?.survey?.id, companyInput, companyId);
    if (!reopened) return false;
    setReviewPhase(false);
    setIsSurveyFinished(false);
    setSurveyConcluded(false);
    setCompanyInput((previous) => ({ ...previous, inputComplete: false, trusteeReview: "user reopen" }));
    return true;
  }, [collectionData?.survey?.id, companyId, companyInput, surveyAdapter]);

  const visibleLoadError = collectionDataErrorScopeKey === collectionScopeKey ? collectionDataLoadError : null;
  const visibleSelectedNoga = hasCurrentCollectionSnapshot ? selectedNoga : emptySelection();
  const visibleSelectedCanton = hasCurrentCollectionSnapshot ? selectedCanton : emptySelection();
  const visibleSelectedOfficeSize = hasCurrentCollectionSnapshot ? selectedOfficeSize : emptySelection();

  return {
    companyOverheads: visibleCollectionData.companyOverheads || [],
    collectionData: visibleCollectionData,
    trusteeName: visibleCollectionData.trustee?.name,
    nogaList: hasCurrentCollectionSnapshot ? nogaList : [],
    cantonsList: hasCurrentCollectionSnapshot ? cantonsList : [],
    officeSizeList: hasCurrentCollectionSnapshot ? officeSizeList : [],
    selectedNoga: visibleSelectedNoga,
    selectedCanton: visibleSelectedCanton,
    selectedOfficeSize: visibleSelectedOfficeSize,
    companyId: hasCurrentCollectionSnapshot ? companyId : null,
    companyInputId: hasCurrentCollectionSnapshot ? visibleCollectionData.companyInputId ?? companyInput?.id ?? null : null,
    isInitialLoad: hasCurrentCollectionSnapshot ? isInitialLoad : false,
    setIsInitialLoad,
    isCollectionDataLoading: isCollectionDataLoading || (canLoadCollectionData && !hasCurrentCollectionSnapshot && !visibleLoadError),
    collectionDataLoadError: visibleLoadError,
    retryCollectionDataLoad,
    isEmployeeFunctionsLoading: hasCurrentCollectionSnapshot ? isEmployeeFunctionsLoading : false,
    employeeFunctionsLoadError: hasCurrentCollectionSnapshot ? employeeFunctionsLoadError : null,
    retryEmployeeFunctions: () => resetEmployeeFunctions(visibleSelectedNoga),
    submitOptions,
    setCollectionData,
    surveyConcluded: hasCurrentCollectionSnapshot ? surveyConcluded : false,
    setSurveyConcluded,
    showEmployeeEntryForm: hasCurrentCollectionSnapshot ? showEmployeeEntryForm : true,
    setShowEmployeeEntryForm,
    employeeEntryFormItem: hasCurrentCollectionSnapshot ? employeeEntryFormItem : {},
    setEmployeeEntryFormItem,
    showEmployeesFileUpload: hasCurrentCollectionSnapshot ? showEmployeesFileUpload : false,
    setShowEmployeesFileUpload,
    finishDataCollection,
    dropdownOptionsEmployees,
    employees: visibleCollectionData.employees || [],
    allEmployeeFunctions: visibleCollectionData.allEmployeeFunctions || [],
    trusteeReview: hasCurrentCollectionSnapshot ? companyInput.trusteeReview : null,
    surveySubmitted: hasCurrentCollectionSnapshot ? companyInput.submitted : false,
    clientReply: hasCurrentCollectionSnapshot ? clientReply : "",
    setClientReply,
    isSubmitModalOpen: hasCurrentCollectionSnapshot ? isSubmitModalOpen : false,
    setIsSubmitModalOpen,
    OnReviewOrConcluded: hasCurrentCollectionSnapshot ? surveyConcluded || reviewPhase : false,
    isSurveyFinished: hasCurrentCollectionSnapshot ? isSurveyFinished : false,
    reopenSubmittedSurvey,
  };
}
